import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Fragment, useRef } from 'react';
import { useHover } from 'react-aria';

import { mergeRefs } from '../../../lib/mergeRefs';
import { ArgumentDelay } from '../flow-arguments/ArgumentDelay';
import { ArgumentDroptoken } from '../flow-arguments/ArgumentDroptoken';
import { ArgumentDuration } from '../flow-arguments/ArgumentDuration';
import { FlowArgument } from '../flow-arguments/FlowArgument';
import { createCard } from '../flow-card/createCard';
import { FlowCardBase } from '../flow-card/FlowCardBase';
import { FlowCardHint } from '../flow-card/FlowCardHint';
import { FlowCardTokensHint } from '../flow-card/FlowCardTokensHint';
import { useTitleChunks } from '../flow-card/useTitleChunks';

import { ResourceUtils } from '../../../store/ResourceUtils';

import { useI18n } from '../../../hooks/useI18nFormatters';

import { su } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import { motion } from '../../../theme/motion';
import { theme } from '../../../theme/theme';

import { ContextMenu } from '../../../components/common/context-menu/ContextMenu';
import { ContextMenuButton } from '../../../components/common/context-menu/ContextMenuButton';
import { Glyph } from '../../../components/common/Glyph';
import { HomeyIcon } from '../../../components/common/HomeyIcon';
import { RouterLink } from '../../../components/common/link/RouterLink';
import { PremiumLocked } from '../../../components/premium/PremiumLocked';

import { flowEditor } from './useFlowEditor';
import { useFlowTest } from './useFlowTest';

const contextMenuButtonClass = 'flowCardContextMenuButton';

export const FlowCard = createCard(FlowCardInner);

function FlowCardInner(props) {
  const { i18n } = useI18n();
  const itemHover = useHover({});
  const cardContainerRef = useRef();

  const { titleChunks, firstArgumentIndex } = useTitleChunks({
    card: props.card,
    data: props.data,
  });

  const testResult = useFlowTest({
    cardType: props.cardType,
    cardIndex: props.cardIndex,
  });

  function mapper(chunk, index) {
    if (chunk.type === 'argument') {
      return (
        <FlowArgument
          key={index}
          cardContainerRef={cardContainerRef}
          argumentKey={chunk.value}
          argument={chunk.argument}
          argumentTypeText={chunk.argumentTypeText}
          otherArguments={chunk.otherArguments}
          cardType={props.cardType}
          cardIndex={props.cardIndex}
          card={props.card}
          data={props.data}
          isFirstArgument={firstArgumentIndex === index}
          onUpdate={(args) => {
            flowEditor.updateCard({
              cardType: props.cardType,
              cardIndex: props.cardIndex,
              argumentKey: args.argumentKey,
              value: args.value,
            });
          }}
        />
      );
    }

    if (chunk.type === 'droptoken') {
      return (
        <ArgumentDroptoken
          key={index}
          cardContainerRef={cardContainerRef}
          argumentTypeText={chunk.argumentTypeText}
          otherArguments={chunk.otherArguments}
          cardType={props.cardType}
          cardIndex={props.cardIndex}
          card={props.card}
          data={props.data}
          isFirstArgument={firstArgumentIndex === index}
          onUpdate={(args) => {
            flowEditor.updateCardProperty({
              cardType: props.cardType,
              cardIndex: props.cardIndex,
              property: 'droptoken',
              value: args.value,
            });
          }}
        />
      );
    }

    if (chunk.type === 'duration') {
      return (
        <ArgumentDuration
          key={index}
          cardContainerRef={cardContainerRef}
          cardType={props.cardType}
          cardIndex={props.cardIndex}
          card={props.card}
          data={props.data}
          onUpdate={(args) => {
            flowEditor.updateCardProperty({
              cardType: props.cardType,
              cardIndex: props.cardIndex,
              property: 'duration',
              value: args.value,
            });
          }}
        />
      );
    }

    if (chunk.type === 'delay') {
      return (
        <ArgumentDelay
          Fragment
          key={index}
          cardContainerRef={cardContainerRef}
          cardType={props.cardType}
          cardIndex={props.cardIndex}
          card={props.card}
          data={props.data}
          onUpdate={(args) => {
            flowEditor.updateCardProperty({
              cardType: props.cardType,
              cardIndex: props.cardIndex,
              property: 'delay',
              value: args.value,
            });
          }}
        />
      );
    }

    return <FlowCardBase.Text key={index}>{chunk.value}</FlowCardBase.Text>;
  }

  function renderTestResult(isContextMenuOpen) {
    if (itemHover.isHovered === false && isContextMenuOpen === false) {
      if (testResult?.state === 'start') {
        return (
          <FlowCard.StatusIconWrapper>
            <Glyph
              source={glyph.core.spinner}
              color={theme.color.icon_dark}
              size={theme.icon.size_medium}
            />
          </FlowCard.StatusIconWrapper>
        );
      }

      if (testResult?.state === 'end' && testResult?.result === true) {
        return (
          <FlowCard.StatusIconWrapper>
            <Glyph
              source={glyph.core.checkmarkCircle}
              color={theme.color.success}
              size={theme.icon.size_medium}
            />
          </FlowCard.StatusIconWrapper>
        );
      }

      if (testResult?.state === 'end' && testResult?.result === false) {
        return (
          <FlowCard.StatusIconWrapper>
            <Glyph
              source={glyph.core.exclamationmarkCircleFill}
              color={theme.color.warning}
              size={theme.icon.size_medium}
            />
          </FlowCard.StatusIconWrapper>
        );
      }

      if (testResult?.state === 'error') {
        return (
          <FlowCard.StatusIconWrapper>
            <Glyph
              source={glyph.core.exclamationmarkTriangleFill}
              color={theme.color.error}
              size={theme.icon.size_medium}
            />
          </FlowCard.StatusIconWrapper>
        );
      }
    }
  }

  function renderContent() {
    if (props.card == null) {
      return (
        <Fragment>
          <FlowCardBase.IconWrapper
            style={{
              '--flow-card-icon-wrapper-background': theme.icon.color_light,
            }}
          >
            <Glyph
              source={glyph.core.questionmarkSmall}
              color={theme.icon.color_white}
              size={theme.icon.size_medium}
            />
          </FlowCardBase.IconWrapper>

          <FlowCardBase.Details>
            <div
              style={{
                display: 'flex',
                flex: '1 1 auto',
                alignItems: 'center',
                gap: 5,
                color: theme.color.text_light,
              }}
            >
              {i18n.messageFormatter('flow.unavailableCard')}
              <FlowCardHint
                hint={
                  <div>
                    <div style={{ paddingBottom: 10 }}>
                      {i18n.messageFormatter('flow.unavailableCardMessage')}
                    </div>
                    <div>{ResourceUtils.getOwnerUri(props.data)}</div>
                    <div>{props.data?.id}</div>
                  </div>
                }
              />
            </div>
          </FlowCardBase.Details>
        </Fragment>
      );
    }

    return (
      <Fragment>
        <FlowCardBase.IconWrapper
          style={{
            '--flow-card-icon-wrapper-background': props.iconBackgroundColor,
          }}
        >
          {(() => {
            if (props.iconComponent != null) {
              return props.iconComponent;
            }

            if (props.iconUrl != null) {
              return (
                <Glyph
                  source={props.iconUrl}
                  color={theme.color.white}
                  size={props.iconSize ?? theme.icon.size_medium}
                />
              );
            }

            return (
              <HomeyIcon
                iconObj={props.iconObj}
                color={theme.color.white}
                size={props.iconSize ?? theme.icon.size_default}
              />
            );
          })()}
        </FlowCardBase.IconWrapper>

        <FlowCardBase.Details>
          <FlowCardBase.Header>
            <FlowCardBase.Name>
              {props.resourceUrl ? (
                <FlowCard.Link to={props.resourceUrl}>{props.name}</FlowCard.Link>
              ) : (
                props.name
              )}
            </FlowCardBase.Name>
            {props.card.hint && <FlowCardHint hint={props.card.hint} />}
            {props.card.tokens != null && props.card.tokens?.length > 0 && (
              <FlowCardTokensHint tokens={props.card.tokens} />
            )}
          </FlowCardBase.Header>
          <FlowCardBase.Content>{titleChunks.map(mapper)}</FlowCardBase.Content>
        </FlowCardBase.Details>
      </Fragment>
    );
  }

  return (
    <ContextMenu content={props.contextMenuContent}>
      {({ isOpen, onContextMenu }) => {
        return (
          <FlowCard.Root
            {...itemHover.hoverProps}
            ref={mergeRefs([props.forwardedRef, cardContainerRef])}
            className={`flow-card ${props.className ?? ''}`}
            data-style-type={props.cardType}
            isDraggingClone={props.isDraggingClone}
            onContextMenu={onContextMenu}
          >
            {props.isLocked === true && <PremiumLocked.Pressable zIndex={2} />}

            {renderContent()}

            {renderTestResult(isOpen)}

            <ContextMenuButton
              className={contextMenuButtonClass}
              isOpen={isOpen}
              onPress={onContextMenu}
            />
          </FlowCard.Root>
        );
      }}
    </ContextMenu>
  );
}

FlowCard.Link = styled(RouterLink)`
  color: ${theme.color.text_light};

  &:hover {
    color: ${theme.color.text_light_hover};
  }
`;

FlowCard.Root = styled.div`
  box-shadow: ${theme.boxShadow.default};
  background-color: ${theme.color.component};
  border-radius: ${theme.borderRadius.default};
  --flow-token-max-width: 240px; // Used in src/containers/flows/flow-arguments/FlowToken.js

  position: relative;
  display: flex;
  align-items: center;
  padding: ${su(1)};
  margin: ${su(2)} 0;
  transition: box-shadow ${motion.duration.micro} ease-in-out;
  width: 340px;
  outline: 0;
  background: ${theme.flowcard.background};

  ${(props) =>
    props.isDraggingClone &&
    css`
      box-shadow: ${theme.boxShadow.drag};
    `}
  .${contextMenuButtonClass} {
    opacity: 0;
    transition: opacity ${motion.transition.micro_partial};
    z-index: 3;
  }

  &:hover {
    .${contextMenuButtonClass} {
      opacity: 1;
    }
  }

  &:not([data-style-type='trigger']) ${FlowCardBase.IconWrapper} {
    cursor: grab;
  }
`;

FlowCard.StatusIconWrapper = styled.div`
  position: absolute;
  transform: translateY(-50%);
  top: 50%;
  right: ${su(1)};
`;

FlowCard.Text = styled.span`
  border: 1px solid transparent;
`;
